/** Utilitaires partagés des écrans d'administration commerciale et ventes (Agent A2b). */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Page } from '@myron/contracts';
import { get } from '../../../shared/api/client';

export interface Entrepot { id: string; code: string; nom: string; retraitActif?: boolean; livraisonActive?: boolean; creneaux?: { id: string; jourSemaine: number; debut: string; fin: string }[] }
export interface Groupe { id: string; code: string; libelle: string; description?: string; nbEntreprises?: number; version: number }
export interface Categorie { id: string; libelle: string; slug: string; parentId?: string | null; ordre: number; version: number }
export interface Marque { id: string; libelle: string; version: number }

/** Entrepôts (module logistique). Dégradation silencieuse si le module n'est pas disponible. */
export function useEntrepots() {
  return useQuery({ queryKey: ['admin', 'entrepots'], queryFn: () => get<Entrepot[]>('/admin/entrepots'), staleTime: 300_000, retry: false });
}
export function useGroupes(actif = true) {
  return useQuery({ queryKey: ['admin', 'tarifs', 'groupes'], queryFn: () => get<Groupe[]>('/admin/tarifs/groupes'), enabled: actif });
}
export function useCategories() {
  return useQuery({ queryKey: ['admin', 'categories'], queryFn: () => get<Categorie[]>('/admin/categories') });
}
export function useMarques() {
  return useQuery({ queryKey: ['admin', 'marques'], queryFn: () => get<Marque[]>('/admin/marques') });
}
/** Petite liste de produits pour les sélecteurs (SKU + libellé). */
export function useProduitsSelecteur() {
  return useQuery({
    queryKey: ['admin', 'produits', 'selecteur'],
    queryFn: () => get<Page<{ id: string; sku: string; libelle: string; conditionnements: { type: string; vendable: boolean }[] }>>('/admin/produits', { taille: 100 }),
    staleTime: 60_000,
  });
}
export function useClientsSelecteur() {
  return useQuery({ queryKey: ['admin', 'clients', 'selecteur'], queryFn: () => get<Page<{ id: string; reference: string; raisonSociale: string; statut: string }>>('/admin/clients', { taille: 100 }), staleTime: 60_000 });
}

export const nomEntrepot = (liste: Entrepot[] | undefined, id?: string | null) => {
  const e = liste?.find((x) => x.id === id);
  return e ? `${e.code} — ${e.nom}` : id ? id.slice(0, 8) : '—';
};

/** Barre de filtres responsive. */
export function BarreFiltres({ children }: { children: ReactNode }) {
  return <div className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'end', marginBottom: 'var(--space-4)' }}>{children}</div>;
}

/** Saisie d'un montant en euros → centimes entiers. */
export const versCentimes = (v: string) => {
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
export const aujourdhuiIso = () => new Date().toISOString().slice(0, 10);
